import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getPartners } from "services/partners/rest";
import { PartnerDto } from "services/partners/restModels";
import { getPayments } from "services/serviceOrders/rest";
import { InvoiceServiceOrderDto, PaymentsPageDto } from "services/serviceOrders/restModels";
import { dateToInputValue, endOfDay, inputValueToDate, startOfDay } from "shared/lib/dateInput";

import { PAGE_SIZE, PaymentsTab } from "../constants";

interface PaymentsPeriod {
    from: string;
    to: string;
}

// Считается на каждый разбор, а не один раз при импорте: у вкладки, открытой сутки, константа
// модуля означала бы уже вчера.
const lastMonthPeriod = (): PaymentsPeriod => {
    const now = new Date();
    const daysInPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    // 31 марта даёт 28 февраля, а не перенос Date на начало марта.
    const day = Math.min(now.getDate(), daysInPreviousMonth);

    return {
        from: dateToInputValue(new Date(now.getFullYear(), now.getMonth() - 1, day)),
        to: dateToInputValue(now),
    };
};

// Разбор терпимый: правленый руками адрес не должен приводить к отказу бэка.
const readFilters = (search: string) => {
    const params = new URLSearchParams(search);
    const defaults = lastMonthPeriod();
    const rawFrom = params.get("from") || "";
    const rawTo = params.get("to") || "";
    const from = inputValueToDate(rawFrom) ? rawFrom : defaults.from;
    const to = inputValueToDate(rawTo) ? rawTo : defaults.to;
    const partnerId = Number(params.get("partnerId"));

    return {
        from: from <= to ? from : to,
        to: from <= to ? to : from,
        partnerId: Number.isInteger(partnerId) && partnerId > 0 ? partnerId : null,
        tab: (params.get("tab") === "payouts" ? "payouts" : "payments") as PaymentsTab,
    };
};

export const usePaymentsState = (isSuperUser: boolean) => {
    const history = useHistory();
    const location = useLocation();

    const [initialFilters] = useState(() => readFilters(location.search));
    const [from, setFrom] = useState(initialFilters.from);
    const [to, setTo] = useState(initialFilters.to);
    const [partnerId, setPartnerId] = useState<number | null>(initialFilters.partnerId);
    const [tab, setTab] = useState<PaymentsTab>(initialFilters.tab);
    const [page, setPage] = useState(1);

    const [data, setData] = useState<PaymentsPageDto | null>(null);
    const [error, setError] = useState(false);
    const [partners, setPartners] = useState<PartnerDto[]>([]);

    const requestId = useRef(0);

    const fromDate = inputValueToDate(from);
    const toDate = inputValueToDate(to);
    // Пока дата недописана или период перевёрнут, на экране остаётся прошлая выборка.
    const isPeriodValid = fromDate !== null && toDate !== null && from <= to;
    const fromTime = isPeriodValid ? startOfDay(fromDate).getTime() : null;
    const toTime = isPeriodValid ? endOfDay(toDate).getTime() : null;

    const loadPayments = useCallback(async () => {
        if (fromTime === null || toTime === null) return;

        const currentRequest = requestId.current + 1;
        requestId.current = currentRequest;

        const response = await getPayments({
            from: fromTime,
            to: toTime,
            partnerId,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
        });

        if (currentRequest !== requestId.current) return;

        if (response) setData(response);
        setError(response === null);
    }, [fromTime, toTime, partnerId, page]);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    useEffect(() => {
        if (!isSuperUser) return undefined;

        let ignore = false;

        getPartners().catch(() => null).then((response) => {
            if (!ignore) setPartners(response || []);
        });

        return () => {
            ignore = true;
        };
    }, [isSuperUser]);

    // Через replace: иначе «назад» браузера отменял бы фильтры по одному.
    useEffect(() => {
        if (!isPeriodValid) return;

        const defaults = lastMonthPeriod();
        const params = new URLSearchParams();

        if (from !== defaults.from) params.set("from", from);
        if (to !== defaults.to) params.set("to", to);
        if (partnerId !== null) params.set("partnerId", String(partnerId));
        if (tab !== "payments") params.set("tab", tab);

        history.replace({ search: params.toString() });
    }, [history, isPeriodValid, from, to, partnerId, tab]);

    const handleInvoiceUpdated = (updated: InvoiceServiceOrderDto) =>
        setData((current) => ({
            ...current,
            items: current.items.map((item) =>
                item.order.type === "Invoice" && item.order.invoiceId === updated.invoiceId
                    ? { ...item, order: updated }
                    : item
            ),
        }));

    const handleChangeFrom = (value: string) => {
        setFrom(value);
        setPage(1);
    };

    const handleChangeTo = (value: string) => {
        setTo(value);
        setPage(1);
    };

    const handleChangePartner = (value: string) => {
        setPartnerId(value ? Number(value) : null);
        setPage(1);
    };

    return {
        from,
        to,
        fromTime,
        toTime,
        partnerId,
        partners,
        tab,
        page,
        pageSize: PAGE_SIZE,
        data,
        error,
        onChangeFrom: handleChangeFrom,
        onChangeTo: handleChangeTo,
        onChangePartner: handleChangePartner,
        onChangePage: setPage,
        onChangeTab: setTab,
        onInvoiceUpdated: handleInvoiceUpdated,
        onRetry: loadPayments,
    };
};
